import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInverse } from '@c2fo/liquidity';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import localeRateToDomain from '@/utils/localeRateToDomain';
import { RateType, useOfferBuilderContext } from '../../OfferBuilderContext';
import { useValidateRate, ValidateRateResult, validateAndNormalizeRate, buildBulkRatePayload } from '../../utils';
import NameYourRateInput from '../NameYourRate/NameYourRateInput';

interface BulkNameYourRateModalProps {
  takerMarkets: TakerMarket[];
  open: boolean;
  onClose: () => void;
}

const BulkNameYourRateModal = ({ takerMarkets, open, onClose }: BulkNameYourRateModalProps) => {
  const { t } = useTranslation();
  const {
    dispatch,
    state: { frequency, rateType: rateTypeFromContext, workingTakerMarkets },
  } = useOfferBuilderContext();
  const { trackEnterpriseEvent } = useReporting();
  const { validateRate } = useValidateRate();
  const rateInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);
  const rateTypeToUse = rateTypeFromContext ?? 'APR';

  const [internalRate, setInternalRate] = useState('');
  const [internalRateType, setInternalRateType] = useState<RateType>(rateTypeToUse);
  const [rateError, setRateError] = useState<ValidateRateResult | null>(null);

  // Initialize state when modal opens
  useEffect(() => {
    if (open && !initializedRef.current) {
      setInternalRate('');
      setInternalRateType(rateTypeToUse);
      setRateError(null);
      initializedRef.current = true;
    } else if (!open) {
      initializedRef.current = false;
    }
  }, [open, rateTypeToUse]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        if (rateInputRef.current) {
          rateInputRef.current.focus();
        }
      });
    }
  }, [open]);

  const handleRateChange = useCallback((value: string) => {
    setInternalRate(value);
    setRateError(null);
  }, []);

  const handleRateBlur = useCallback(() => {
    const domainRate = localeRateToDomain(internalRate) ?? '';

    if (domainRate && internalRateType) {
      const validationResult = validateRate(domainRate, internalRateType);

      if (!validationResult.valid) {
        setRateError(validationResult);
      }
    }
  }, [internalRate, internalRateType, validateRate]);

  const handleRateTypeChange = useCallback((value: RateType) => {
    setInternalRateType(value);
    setInternalRate('');
  }, []);

  const handleApply = () => {
    // Validate and normalize rate input
    const domainRate = localeRateToDomain(internalRate) ?? '';
    const result = validateAndNormalizeRate(domainRate, internalRateType, validateRate, t);

    if (!result.success) {
      if (result.errorMessage) {
        setRateError({ valid: false, errorMessage: result.errorMessage });
      }

      return;
    }

    const normalizedRate = result.normalizedRate!;

    // Clear any validation errors
    setRateError(null);

    // Build the payload - override all markets with the new rate
    const payload = buildBulkRatePayload(
      takerMarkets,
      'nameYourRate',
      internalRateType,
      frequency,
      undefined,
      workingTakerMarkets,
      normalizedRate,
      false // don't skip any markets
    );

    // Track event
    trackEnterpriseEvent('offer-builder::bulk-rate::applied', {
      rate: normalizedRate,
      rateType: internalRateType,
      pricingStrategy: 'nameYourRate',
      frequency,
      totalMarkets: takerMarkets.length,
      marketsUpdated: payload.length,
    });

    // Dispatch updates
    dispatch({
      type: 'UPDATE_WORKING_TAKER_MARKETS_RATE',
      payload,
    });

    onClose();
  };

  const handleClose = useCallback(() => {
    trackEnterpriseEvent('offer-builder::bulk-name-your-rate-modal::closed', {
      hadInput: internalRate.trim() !== '',
    });
    onClose();
  }, [internalRate, onClose, trackEnterpriseEvent]);

  return (
    <Modal onClose={handleClose} open={open} size="sm">
      <ModalTitleInverse>{t('builder.nameYourRateApplyAll')}</ModalTitleInverse>
      <ModalContent className="pt-0">
        <div className="mb-4">{t('builder.setRateForAllDivisions')}</div>
        <NameYourRateInput
          internalRate={internalRate}
          internalRateType={internalRateType}
          rateError={rateError}
          rateTypeError={null}
          onRateChange={handleRateChange}
          onRateBlur={handleRateBlur}
          onRateTypeChange={handleRateTypeChange}
          rateInputRef={rateInputRef}
        />
        <div className="mt-4">{t('builder.noteAnyExistingRatesWillBeOverridden')}</div>
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          {t('core.back')}
        </Button>
        <Button variant="primary" onClick={handleApply}>
          {t('core.apply')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default BulkNameYourRateModal;
